import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectMeetingInfoHTML, selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import {
  selectMeetingNote,
  selectMeetingNoteIsLoading,
  selectMeetingNoteUpdateIsLoading,
  setMeetingNoteChangedExternally,
} from '../../../../../store/meetingSummary/meetingSupport'
import { useUpdateMeetingNote } from '../../MeetingSummary/hooks/api'
import {
  addHtmlContentToState,
  getHtmlContentFromState,
  getTextContentFromState,
  parseIntoHeadlessEditor,
} from '../../MeetingSummary/MeetingResults/Lexical/utils'
import { showToast } from '../../../../../store/meetingSummary/display'
import { HeaderResultOption } from '../../MeetingSummary/MeetingResults/ResultCard/Header/HeaderResultOptions/HeaderResultOptionsButton'
import { extendCopyContentWithComplianceMessage, HTML_TYPE, TEXT_TYPE, writeCopyContentToClipboard } from '../../../../../common/copy'
import { useEffectiveConfig } from '../../../../../common/useEffectiveConfig'

export const useAddMeetingInfoNotepadResultOption = (): HeaderResultOption | null => {
  const dispatch = useAppDispatch()
  const roomSession = useAppSelector(selectRoomSession)
  const meetingNote = useAppSelector(selectMeetingNote)
  const updateMeetingNote = useUpdateMeetingNote()
  const meetingInfoHTML = useAppSelector(selectMeetingInfoHTML)
  const isLoading = useAppSelector(selectMeetingNoteIsLoading)
  const isMeetingNoteUpdateLoading = useAppSelector(selectMeetingNoteUpdateIsLoading)
  const disabled = isLoading || isMeetingNoteUpdateLoading || !meetingNote

  const handleAddMeetingInfoClicked = async () => {
    const headlessEditor = parseIntoHeadlessEditor(meetingNote)
    addHtmlContentToState(headlessEditor, meetingInfoHTML)
    const newMeetingNote = JSON.stringify(headlessEditor.getEditorState().toJSON())
    await updateMeetingNote(newMeetingNote)
    dispatch(setMeetingNoteChangedExternally(true))
    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'Added to notepad!' }))
  }

  return roomSession
    ? {
        selectOption: {
          id: 'addMeetingInfo',
          label: 'Add meeting info',
          disabled,
        },
        icon: 'bi bi-info-circle-fill',
        handleSelected: handleAddMeetingInfoClicked,
      }
    : null
}

export const useCopyNotepadResultOption = (): HeaderResultOption | null => {
  const dispatch = useAppDispatch()

  const meetingNote = useAppSelector(selectMeetingNote)
  const isLoading = useAppSelector(selectMeetingNoteIsLoading)
  const isMeetingNoteUpdateLoading = useAppSelector(selectMeetingNoteUpdateIsLoading)

  const { copyComplianceMessage, accountIsFetching } = useEffectiveConfig()

  const disabled = isLoading || isMeetingNoteUpdateLoading || !meetingNote || accountIsFetching

  const handleClick = async () => {
    const textContent = getTextContentFromState(meetingNote)
    const htmlContent = getHtmlContentFromState(meetingNote)

    const _copyContent = { [TEXT_TYPE]: textContent, [HTML_TYPE]: htmlContent }
    const copyContent = extendCopyContentWithComplianceMessage(_copyContent, copyComplianceMessage)

    await writeCopyContentToClipboard(copyContent)

    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'Copied to clipboard!' }))
  }

  return {
    selectOption: {
      id: 'copy',
      label: 'Copy',
      disabled,
    },
    icon: 'bi bi-copy',
    handleSelected: handleClick,
  }
}
